// src/pages/OrganizationSettingsPage.jsx

import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Tabs,
  Tab,
  Grid,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useLocation, useNavigate } from "react-router-dom";
import TimezoneSelect from "react-timezone-select";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { getCurrentUser } from "../services/authService";
import { updateOrganizationSettings } from "../services/organizationService";
import { useTimezone } from "../contexts/TimezoneContext";
import BillingTab from "../components/BillingTab";
import RoomFieldsTab from "../components/RoomFieldsTab";
import CountrySelect from "../components/CountrySelect";
import OrganizationLogoManager from "../components/OrganizationLogoManager";
import { useDispatch } from "react-redux";
import { updateOrganizationLogo, updateOrganizationTimezone } from "../userSlice";

export default function OrganizationSettingsPage() {
  // short English comments only
  const [orgName, setOrgName] = useState("");
  const [timeZone, setTimeZone] = useState("UTC");
  const [startTime, setStartTime] = useState(dayjs("2025-01-01T08:00"));
  const [endTime, setEndTime] = useState(dayjs("2025-01-01T18:00"));
  const [currentTab, setCurrentTab] = useState(0);
  const [organizationId, setOrganizationId] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [companyAddress, setCompanyAddress] = useState({
    line1: '',
    line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: ''
  });

  const { enqueueSnackbar } = useSnackbar();
  const { setOrgTimeZone } = useTimezone();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Tab configuration with anchors
  const tabs = useMemo(() => [
    { label: "General", anchor: "general" },
    { label: "Billing", anchor: "billing" },
    { label: "Room Fields", anchor: "room-fields" }
  ], []);

  // Get tab index from URL hash
  const getTabFromHash = useCallback((hash) => {
    const anchor = hash.replace('#', '');
    const tabIndex = tabs.findIndex(tab => tab.anchor === anchor);
    return tabIndex >= 0 ? tabIndex : 0;
  }, [tabs]);

  // Set URL hash from tab index
  const setHashFromTab = useCallback((tabIndex) => {
    const anchor = tabs[tabIndex]?.anchor || tabs[0].anchor;
    navigate(`#${anchor}`, { replace: true });
  }, [tabs, navigate]);

  // Handle URL hash changes and set initial tab
  useEffect(() => {
    const tabIndex = getTabFromHash(location.hash);
    setCurrentTab(tabIndex);
    
    // Set default hash if none exists
    if (!location.hash) {
      setHashFromTab(0);
    }
  }, [location.hash, getTabFromHash, setHashFromTab]);

  useEffect(() => {
    getCurrentUser()
      .then((user) => {
        if (user && user.organization) {
          // Store the full organization object
          setOrganization(user.organization);
          
          // Set organization ID for billing tab
          setOrganizationId(user.organization._id);
          
          if (user.organization.name) setOrgName(user.organization.name);
          if (user.organization.timeZone)
            setTimeZone(user.organization.timeZone);

          // Load company address
          if (user.organization.companyAddress) {
            setCompanyAddress(user.organization.companyAddress);
          }

          if (user.organization.defaultStartTime) {
            const str = user.organization.defaultStartTime;
            const dayjsObj = dayjs(`2025-01-01T${str}`);
            setStartTime(
              dayjsObj.isValid() ? dayjsObj : dayjs("2025-01-01T08:00")
            );
          }
          if (user.organization.defaultEndTime) {
            const str = user.organization.defaultEndTime;
            const dayjsObj = dayjs(`2025-01-01T${str}`);
            setEndTime(
              dayjsObj.isValid() ? dayjsObj : dayjs("2025-01-01T18:00")
            );
          }
        }
      })
      .catch(() => {
        enqueueSnackbar("Could not fetch organization info", {
          variant: "error",
        });
      });
  }, [enqueueSnackbar]);

  async function handleSave(e) {
    e.preventDefault();
    try {
      const formatStart = startTime.format("HH:mm");
      const formatEnd = endTime.format("HH:mm");

      const result = await updateOrganizationSettings({
        name: orgName,
        timeZone,
        defaultStartTime: formatStart,
        defaultEndTime: formatEnd,
        companyAddress: companyAddress,
      });
      
      // Update the timezone context with the new timezone
      setOrgTimeZone(timeZone);
      
      // Update the Redux store with the new timezone
      dispatch(updateOrganizationTimezone(timeZone));
      
      enqueueSnackbar(result.message || "Updated successfully", {
        variant: "success",
      });
    } catch (err) {
      if (err.response && err.response.data?.error) {
        enqueueSnackbar(err.response.data.error, { variant: "error" });
      } else {
        enqueueSnackbar("Failed to update organization settings", {
          variant: "error",
        });
      }
    }
  }

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setHashFromTab(newValue);
  };

  const handleCompanyAddressChange = (e) => {
    const { name, value } = e.target;
    setCompanyAddress(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogoUpdate = (newLogo) => {
    setOrganization(prev => ({
      ...prev,
      logo: newLogo
    }));
    // Update Redux state so navbar shows the new logo immediately
    dispatch(updateOrganizationLogo(newLogo));
  };

  // General Settings Tab Component
  const GeneralSettingsTab = () => (
    <Box component="form" onSubmit={handleSave} sx={{ mt: 2 }}>
      <TextField
        label="Organization Name"
        variant="outlined"
        fullWidth
        sx={{ mb: 3 }}
        value={orgName}
        onChange={(e) => setOrgName(e.target.value)}
      />

      {/* Organization Logo Manager */}
      <Box sx={{ mb: 4 }}>
        <OrganizationLogoManager 
          organization={organization} 
          onLogoUpdate={handleLogoUpdate}
        />
      </Box>

      <Typography variant="body1" sx={{ mb: 1 }}>
        Time Zone
      </Typography>
      <Box sx={{ mb: 3 }}>
        <TimezoneSelect
          value={timeZone}
          onChange={(tz) => setTimeZone(tz.value)}
        />
      </Box>

      {/* Company Address Section */}
      <Typography variant="h6" sx={{ mb: 2, mt: 4 }}>
        Company Address
      </Typography>
      
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Address Line 1"
            name="line1"
            value={companyAddress.line1}
            onChange={handleCompanyAddressChange}
            placeholder="Street address"
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Address Line 2 (Optional)"
            name="line2"
            value={companyAddress.line2}
            onChange={handleCompanyAddressChange}
            placeholder="Apartment, suite, etc."
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="City"
            name="city"
            value={companyAddress.city}
            onChange={handleCompanyAddressChange}
            placeholder="City"
          />
        </Grid>
        
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label="State/Province"
            name="state"
            value={companyAddress.state}
            onChange={handleCompanyAddressChange}
            placeholder="State"
          />
        </Grid>
        
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label="Postal Code"
            name="postal_code"
            value={companyAddress.postal_code}
            onChange={handleCompanyAddressChange}
            placeholder="ZIP/Postal code"
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <CountrySelect
            value={companyAddress.country}
            onChange={(value) => setCompanyAddress(prev => ({ ...prev, country: value }))}
          />
        </Grid>
      </Grid>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <TimePicker
          label="Default Start Time"
          value={startTime}
          onChange={(newValue) => {
            if (newValue) setStartTime(newValue);
          }}
          slots={{
            textField: TextField
          }}
          slotProps={{
            textField: {
              sx: { mb: 3 },
              fullWidth: true
            }
          }}
        />

        <TimePicker
          label="Default End Time"
          value={endTime}
          onChange={(newValue) => {
            if (newValue) setEndTime(newValue);
          }}
          slots={{
            textField: TextField
          }}
          slotProps={{
            textField: {
              sx: { mb: 3 },
              fullWidth: true
            }
          }}
        />
      </LocalizationProvider>

      <Box>
        <Button variant="contained" type="submit">
          Save
        </Button>
      </Box>
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Paper sx={{ p: 3 }} elevation={2}>
        <Typography variant="h5" gutterBottom>
          Organization Settings
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={currentTab} onChange={handleTabChange}>
            {tabs.map((tab, index) => (
              <Tab key={tab.anchor} label={tab.label} />
            ))}
          </Tabs>
        </Box>

        {currentTab === 0 && <GeneralSettingsTab />}
        {currentTab === 1 && (
          <BillingTab organizationId={organizationId} />
        )}
        {currentTab === 2 && <RoomFieldsTab />}
      </Paper>
    </Container>
  );
}
